import IconButton from "@/components/Button/IconButton";
import ResponseMessage from "@/components/Error/ResponseMessage";
import Input from "@/components/Input/Input";
import { HStack, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { useHighlyConfidentialValue } from "../../hooks/useHighlyConfidentialValue";
import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from "../CFormItem";

type Props = CFormItemProps & {
  value?: string;
  onChange?: (value?: string) => void;
  inputWidth?: number | string;
  inputClassName?: string;
  /**
   * 機密値を取得するための関数
   * 目のアイコンをクリックした際にこの関数が呼ばれます
   * @returns 復号化された機密値（文字列）
   */
  fetchConfidentialValue: () => Promise<string>;
  /**
   * マスク表示する文字列（デフォルト: "***"）
   */
  maskText?: string;
  /**
   * 値が存在するかどうか
   * trueの場合、マスクとアイコンが表示されます
   */
  hasValue?: boolean;
};

/**
 * Card-based Form / HighlyConfidentialTextItem
 *
 * 高機密テキストフィールド用のCFormアイテム
 * デフォルトでマスク表示され、目のアイコンをクリックすると実際の値が表示されます
 */
export const CFormHighlyConfidentialTextItem = buildCFormItem(
  (props: Props) => {
    const {
      value,
      onChange,
      inputWidth,
      inputClassName,
      fetchConfidentialValue,
      maskText = "***",
      hasValue,
      ...baseProps
    } = props;

    // 秘匿値の状態管理
    const { isRevealed, secretValue, isLoading, error, toggleReveal, reset } =
      useHighlyConfidentialValue({
        fetchConfidentialValue,
      });

    // 編集モードの変化を追跡
    const prevIsEditingRef = useRef(baseProps.isEditing);

    // 編集モードから読み取り専用モードに戻った時（保存完了時）に秘匿値をリセット
    useEffect(() => {
      if (prevIsEditingRef.current && !baseProps.isEditing) {
        // 編集モードから読み取り専用モードに変わった = 保存完了
        reset();
      }
      prevIsEditingRef.current = baseProps.isEditing;
    }, [baseProps.isEditing, reset]);

    // 値の存在判定
    const actualHasValue = hasValue ?? (value !== undefined && value !== "");

    // 表示値の計算
    let displayValue: string | ReactNode = "";
    if (isRevealed && secretValue) {
      displayValue = secretValue; // 復号化された実際の値
    } else if (actualHasValue) {
      displayValue = maskText; // マスク表示
    }

    // ReadOnlyモードの表示コンポーネント
    const readComponent = (
      <VStack className="gap-1">
        <HStack className="gap-2 items-center">
          <Text variant="labelSemiLarge" className="font-mono">
            {displayValue}
          </Text>
          {actualHasValue && (
            <IconButton
              variant="ghost"
              size="ExtraSmall"
              onClick={toggleReveal}
              icon={isRevealed ? "Eye" : "EyeOff"}
              disabled={isLoading}
              aria-label={isRevealed ? "Hide value" : "Show value"}
            />
          )}
        </HStack>
        {error && (
          <ResponseMessage type="error" className="text-sm">
            {error.message}
          </ResponseMessage>
        )}
      </VStack>
    );

    // 編集モードの入力コンポーネント
    const writeComponent = (
      <Input
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value || undefined)}
        width={inputWidth}
        className={inputClassName}
        disabled={props.readonly}
      />
    );

    return (
      <CFormItemBase
        {...baseProps}
        readComponent={readComponent}
        writeComponent={writeComponent}
      />
    );
  },
);
